import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import JiraKeyChip from '../JiraKeyChip';
import { STANDARDS } from '../compliance/types';
import Block, { LINK_BUTTON } from './Block';
import { MemberHomeData } from './types';
import { achieveHeadline, doneHeadline, goalLine, hoursHeadline, nextHeadline, noLogSentence, personPath, toFixBreakdown, toFixHeadline } from './sentences';
import { PLAN_STATUS_LABEL, planStatusStyle } from '../plan/types';
import * as fmt from '../../lib/format';

const LABEL = Object.fromEntries(STANDARDS.map((s) => [s.id, s.label]));

// What a team member needs to fix: the total, the top few, and one way to see them all.
export function ToFixBlock({ toFix }: { toFix: MemberHomeData['to_fix'] }) {
  const { total, by_standard, items } = toFix;
  return (
    <Block title="To fix" className="lg:col-span-3">
      <p className="text-3xl font-semibold tracking-tight text-gray-900">{toFixHeadline(total)}</p>
      {total > 0 && <p className="mt-1 text-sm text-gray-600">{toFixBreakdown(by_standard)}</p>}

      {items.length > 0 ? (
        <ul className="mt-4 divide-y divide-gray-100">
          {items.map((i) => (
            <li key={`${i.standard}:${i.issue_key}`} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <JiraKeyChip issueKey={i.issue_key} url={i.jira_url} />
                {/* The title is what the person has to fix, so it is never cut with an ellipsis, at any text size: it wraps, and drops onto its own line when the chip and the label leave it too little room. */}
                <span className="min-w-0 grow basis-48 break-words text-sm font-medium text-gray-900">{i.issue_name ?? i.issue_key}</span>
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">{LABEL[i.standard]}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{i.why}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900 ring-1 ring-inset ring-emerald-100">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          Your ticket titles, moves and time entries all look good.
        </p>
      )}

      <Link to="/my-audit" className={LINK_BUTTON}>
        {total > items.length ? `See all ${fmt.count(total)} in My audit` : 'Open My audit'}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Block>
  );
}

// What a person is to achieve this quarter (the Plan's goals) and what is next (open tickets in a sprint that is running).
export function QuarterBlock({ quarter }: { quarter: MemberHomeData['quarter'] }) {
  const { achieve, next } = quarter;
  return (
    <Block title={`Your quarter: ${quarter.label}`} className="lg:col-span-2">
      <p className="text-3xl font-semibold tracking-tight text-gray-900">{achieveHeadline(achieve.total)}</p>
      {achieve.items.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100">
          {achieve.items.map((g) => {
            const style = planStatusStyle(g.plan_status);
            return (
              <li key={g.issue_key} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <JiraKeyChip issueKey={g.issue_key} url={g.jira_url} />
                  <span className="min-w-0 grow basis-48 break-words text-sm font-medium text-gray-900">{g.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset ${style.badge}`}>{PLAN_STATUS_LABEL[g.plan_status]}</span>
                </div>
                <p className="mt-1 text-sm text-gray-600">{goalLine(g)}</p>
              </li>
            );
          })}
        </ul>
      )}
      <h3 className="mt-5 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Next</h3>
      <p className="mt-1 text-sm text-gray-700">{nextHeadline(next.total)}</p>
      {next.items.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100">
          {next.items.map((t) => (
            <li key={t.issue_key} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 first:pt-0 last:pb-0">
              <JiraKeyChip issueKey={t.issue_key} url={t.jira_url} />
              <span className="min-w-0 grow basis-48 break-words text-sm text-gray-900">{t.issue_name ?? t.issue_key}</span>
              {t.status && <span className="text-xs text-gray-600">{t.status}</span>}
            </li>
          ))}
        </ul>
      )}
      <Link to="/plan" className={LINK_BUTTON}>
        Open the plan
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Block>
  );
}

// What was finished this month, and the hours behind it.
export function DoneBlock({ done, hours, accountId }: { done: MemberHomeData['done']; hours: MemberHomeData['hours']; accountId: string }) {
  return (
    <Block title="Done this month">
      <p className="text-3xl font-semibold tracking-tight text-gray-900">{doneHeadline(done.total)}</p>
      {done.items.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100">
          {done.items.map((t) => (
            <li key={t.issue_key} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 first:pt-0 last:pb-0">
              <JiraKeyChip issueKey={t.issue_key} url={t.jira_url} />
              <span className="min-w-0 grow basis-40 break-words text-sm text-gray-900">{t.issue_name ?? t.issue_key}</span>
            </li>
          ))}
        </ul>
      )}
      {hours ? (
        <>
          <p className="mt-4 text-sm font-medium text-gray-900">{hoursHeadline(hours.logged_hours)}</p>
          <p className="mt-1 text-sm text-gray-700">{noLogSentence(hours.no_log_days, hours.no_log_count)}</p>
          <Link to={personPath(accountId)} className={LINK_BUTTON}>
            Open my month
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </>
      ) : (
        <p className="mt-4 text-sm text-gray-600">Your hours are not tracked in the portal, so there is nothing to show here.</p>
      )}
    </Block>
  );
}
